import { type ModuleCode, type Permission } from '@ibe/constants';

/**
 * The signed-in staff member, as the portal needs them.
 *
 * A projection of the JWT's claims rather than a copy of the user record. The
 * portal needs a name to show, a permission list to render against and the
 * property scope to filter by; it has no business holding anything else about
 * a colleague.
 */
export interface AuthenticatedUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly roles: readonly string[];
  readonly permissions: readonly Permission[];
  /**
   * Properties this user is scoped to. Empty means every property — the
   * platform's convention for an unscoped, organisation-wide role.
   */
  readonly propertyIds: readonly string[];
  /**
   * The modules this user's tenant has bought, `CORE` excluded.
   *
   * `string[]` rather than `ModuleCode[]` deliberately: the list is served,
   * and a client-side enum would reject a module the platform shipped after
   * this bundle was built. The tenants module types `enabledModules` the same
   * way, for the same reason.
   *
   * **Empty means unrestricted, not "nothing bought"** — a platform operator
   * belongs to no tenant and so has no module list, exactly as an empty
   * `propertyIds` means every property rather than none.
   */
  readonly modules: readonly string[];
  /** Empty for a platform operator, who belongs to no tenant. */
  readonly tenantId?: string;
}

export interface LoginRequest {
  readonly email: string;
  readonly password: string;
  /**
   * Extends the refresh cookie's lifetime.
   *
   * Sent to the server rather than stored here, because the thing whose
   * lifetime it governs is a cookie the server sets and no script can read.
   * A client-side "remember me" flag would be a preference with no effect.
   */
  readonly rememberMe: boolean;
}

export interface LoginResponse {
  readonly accessToken: string;
  readonly user: AuthenticatedUser;
}

/**
 * Whether the user holds a permission.
 *
 * **This decides what is shown, never what is allowed.** Every guarded
 * endpoint checks the same permission server-side, and it is the server's
 * answer that matters. Hiding a button the user cannot use is courtesy; it is
 * not a security control, and treating it as one is how an API ends up
 * unguarded because "the UI doesn't show it".
 *
 * The check deliberately mirrors only the part of `@ibe/auth`'s RBAC that a
 * browser can evaluate: membership in the granted list, plus property scope.
 * Anything finer — resource ownership, reservation access — depends on data
 * the client does not have, so the portal shows the control and lets the
 * request be refused with a reason.
 */
export function can(
  user: AuthenticatedUser | null,
  permission: Permission,
  propertyId?: string,
): boolean {
  if (user === null) return false;
  if (!user.permissions.includes(permission)) return false;

  if (propertyId === undefined) return true;
  // An empty scope is organisation-wide, not "no properties". Reading it the
  // other way locks every unscoped administrator out of every property.
  if (user.propertyIds.length === 0) return true;

  return user.propertyIds.includes(propertyId);
}

/**
 * Whether the user's tenant has a module.
 *
 * Presentation, like `can`. It decides whether a control is worth showing, not
 * whether the platform will accept the value behind it — the server caps a
 * tenant's permissions by its modules at login, and that cap is what enforces
 * anything.
 *
 * Empty is treated as unrestricted for the reason given on the field: it is
 * what a platform operator's session carries, and hiding every module-gated
 * control from the one account that operates the platform is the wrong way
 * round.
 */
export function hasModule(user: AuthenticatedUser | null, module: ModuleCode): boolean {
  if (user === null) return false;
  if (user.modules.length === 0) return true;

  return user.modules.includes(module);
}

/** Whether the user holds every one of these permissions. */
export function canAll(
  user: AuthenticatedUser | null,
  permissions: readonly Permission[],
): boolean {
  return permissions.every((permission) => can(user, permission));
}

/** Whether the user holds at least one of these permissions. */
export function canAny(
  user: AuthenticatedUser | null,
  permissions: readonly Permission[],
): boolean {
  return permissions.some((permission) => can(user, permission));
}

/**
 * The response from `/v1/auth/login` when MFA is required.
 *
 * No access token is issued at this stage. The `mfaChallengeToken` is a
 * short-lived, single-purpose token whose only audience is the MFA
 * completion endpoints — it cannot be used as a normal access token.
 */
export interface MfaChallengeResponse {
  readonly mfaChallengeToken: string;
  /** `'enroll'` on first login, `'verify'` on every login after that. */
  readonly mfaRequired: 'enroll' | 'verify';
}

/**
 * The union returned by `/v1/auth/login`. The frontend discriminates on the
 * presence of `mfaChallengeToken` to decide whether to show the MFA step.
 */
export type LoginResult = LoginResponse | MfaChallengeResponse;

/** Whether a login result is an MFA challenge rather than a full session. */
export function isMfaChallenge(result: LoginResult): result is MfaChallengeResponse {
  return 'mfaChallengeToken' in result;
}

/** Response from `/v1/auth/mfa/setup`: the QR code, secret and recovery codes. */
export interface MfaSetupResponse {
  readonly qrCodeDataUrl: string;
  readonly secret: string;
  readonly recoveryCodes: readonly string[];
}
